import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from '@/hooks/useTranslation';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Indent,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Outdent,
  Quote,
  Redo,
  Strikethrough,
  Table as TableIcon,
  Type,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react';
import React, { useEffect } from 'react';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  onEditorReady?: (insertText: (text: string) => void) => void;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  disabled = false,
  placeholder = '',
  className = '',
  minHeight = '200px',
  onEditorReady,
}) => {
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable extensions we want to configure separately
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-md',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editable: !disabled,
    editorProps: {
      attributes: {
        class: `prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none p-4`,
        style: `min-height: ${minHeight}`,
        'data-placeholder': placeholder,
      },
    },
  });

  // Expose insertText function to parent component
  useEffect(() => {
    if (editor && onEditorReady) {
      const insertText = (text: string) => {
        editor.chain().focus().insertContent(text).run();
      };
      onEditorReady(insertText);
    }
  }, [editor, onEditorReady]);

  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt(t('richTextEditor.prompts.imageUrl'));
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt(t('richTextEditor.prompts.linkUrl'), previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  const insertHorizontalRule = () => {
    editor.chain().focus().setHorizontalRule().run();
  };

  return (
    <div className={`border rounded-lg bg-background ${className}`}>
      {/* Toolbar */}
      <div className='border-b p-2 flex flex-wrap items-center gap-1 bg-muted/30'>
        {/* Undo/Redo */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo() || disabled}
            title={t('richTextEditor.toolbar.undo')}
          >
            <Undo className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo() || disabled}
            title={t('richTextEditor.toolbar.redo')}
          >
            <Redo className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Headings */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().setParagraph().run()}
            data-active={editor.isActive('paragraph')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.paragraph')}
          >
            <Type className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            data-active={editor.isActive('heading', { level: 1 })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.heading1')}
          >
            <Heading1 className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            data-active={editor.isActive('heading', { level: 2 })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.heading2')}
          >
            <Heading2 className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            data-active={editor.isActive('heading', { level: 3 })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.heading3')}
          >
            <Heading3 className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Text Formatting */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleBold().run()}
            data-active={editor.isActive('bold')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.bold')}
          >
            <Bold className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleItalic().run()}
            data-active={editor.isActive('italic')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.italic')}
          >
            <Italic className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            data-active={editor.isActive('underline')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.underline')}
          >
            <UnderlineIcon className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleStrike().run()}
            data-active={editor.isActive('strike')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.strikethrough')}
          >
            <Strikethrough className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleCode().run()}
            data-active={editor.isActive('code')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.code')}
          >
            <Code className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Alignment */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            data-active={editor.isActive({ textAlign: 'left' })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.alignLeft')}
          >
            <AlignLeft className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            data-active={editor.isActive({ textAlign: 'center' })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.alignCenter')}
          >
            <AlignCenter className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            data-active={editor.isActive({ textAlign: 'right' })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.alignRight')}
          >
            <AlignRight className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            data-active={editor.isActive({ textAlign: 'justify' })}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.alignJustify')}
          >
            <AlignJustify className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Lists */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            data-active={editor.isActive('bulletList')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.bulletList')}
          >
            <List className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            data-active={editor.isActive('orderedList')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.orderedList')}
          >
            <ListOrdered className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() =>
              editor.chain().focus().sinkListItem('listItem').run()
            }
            disabled={!editor.can().sinkListItem('listItem') || disabled}
            title={t('richTextEditor.toolbar.indent')}
          >
            <Indent className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() =>
              editor.chain().focus().liftListItem('listItem').run()
            }
            disabled={!editor.can().liftListItem('listItem') || disabled}
            title={t('richTextEditor.toolbar.outdent')}
          >
            <Outdent className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Quote and HR */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            data-active={editor.isActive('blockquote')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.blockquote')}
          >
            <Quote className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={insertHorizontalRule}
            disabled={disabled}
            title={t('richTextEditor.toolbar.horizontalRule')}
          >
            <Minus className='h-4 w-4' />
          </Button>
        </div>

        <Separator orientation='vertical' className='h-6' />

        {/* Link, Image, Table */}
        <div className='flex items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={addLink}
            data-active={editor.isActive('link')}
            className='data-[active=true]:bg-muted'
            disabled={disabled}
            title={t('richTextEditor.toolbar.link')}
          >
            <LinkIcon className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={addImage}
            disabled={disabled}
            title={t('richTextEditor.toolbar.image')}
          >
            <ImageIcon className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={insertTable}
            disabled={disabled}
            title={t('richTextEditor.toolbar.table')}
          >
            <TableIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className={`
          [&_.ProseMirror]:outline-none 
          [&_.ProseMirror]:border-none 
          [&_.ProseMirror]:bg-transparent
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0
          [&_.ProseMirror_table]:border-collapse-collapse
          [&_.ProseMirror_table_td]:border
          [&_.ProseMirror_table_th]:border
          [&_.ProseMirror_table_td]:border-border
          [&_.ProseMirror_table_th]:border-border
          [&_.ProseMirror_table_td]:p-2
          [&_.ProseMirror_table_th]:p-2
          [&_.ProseMirror_table_th]:bg-muted
          [&_.ProseMirror_table_th]:font-semibold
          [&_.ProseMirror_img]:rounded-md
          [&_.ProseMirror_img]:max-w-full
          [&_.ProseMirror_img]:h-auto
          [&_.ProseMirror_blockquote]:border-l-4
          [&_.ProseMirror_blockquote]:border-border
          [&_.ProseMirror_blockquote]:pl-4
          [&_.ProseMirror_blockquote]:italic
          [&_.ProseMirror_hr]:border-border
          [&_.ProseMirror_hr]:my-4
          [&_.ProseMirror_code]:bg-muted
          [&_.ProseMirror_code]:px-1
          [&_.ProseMirror_code]:rounded
          [&_.ProseMirror_code]:text-sm
          [&_.ProseMirror_pre]:bg-muted
          [&_.ProseMirror_pre]:p-4
          [&_.ProseMirror_pre]:rounded
          [&_.ProseMirror_pre]:overflow-x-auto
        `}
      />
    </div>
  );
};

export default RichTextEditor;
